import { MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { CustomToolStep } from '@/types/custom-tool.types';
import { useCustomToolBuilder } from '../builder/builder-context';
import { canWaitFor, dataSourcesOf, withAfter, withoutStep } from '../custom-tool-draft';
import { useStepName } from './step-name';

/** What a step offers besides its arguments: waiting for another step, and removing it. */
export const StepMenu = ({ step, index, className }: { step: CustomToolStep; index: number; className?: string }) => {
	const { draft, change, focusOn } = useCustomToolBuilder();
	const label = useStepName();
	const dataFrom = new Set(dataSourcesOf(step));
	const others = draft.steps.filter(
		(other) => other.id !== step.id && (dataFrom.has(other.id) || step.after.includes(other.id) || canWaitFor(draft, step.id, other.id))
	);
	const setWait = (otherId: string, wait: boolean) =>
		change((current) => {
			const after = current.steps.find((candidate) => candidate.id === step.id)?.after ?? [];
			return withAfter(current, step.id, wait ? [...after, otherId] : after.filter((id) => id !== otherId));
		});

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant='ghost' size='icon-sm' aria-label={`Step ${index + 1} options`} className={cn('size-6 shrink-0', className)}>
					<MoreHorizontal className='size-4' />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align='end' className='w-56'>
				{others.length > 0 && (
					<DropdownMenuSub>
						<DropdownMenuSubTrigger className='text-[13px]'>Run after</DropdownMenuSubTrigger>
						<DropdownMenuSubContent className='w-64'>
							{others.map((other) => (
								<DropdownMenuCheckboxItem
									key={other.id}
									className='text-[13px]'
									checked={dataFrom.has(other.id) || step.after.includes(other.id)}
									disabled={dataFrom.has(other.id)}
									onCheckedChange={(checked) => setWait(other.id, checked === true)}
									onSelect={(event) => event.preventDefault()}
								>
									<span className='truncate'>{dataFrom.has(other.id) ? `${label(other)} (takes its data)` : label(other)}</span>
								</DropdownMenuCheckboxItem>
							))}
						</DropdownMenuSubContent>
					</DropdownMenuSub>
				)}
				<DropdownMenuItem
					variant='destructive'
					className='text-[13px]'
					onSelect={() => {
						change((current) => withoutStep(current, step.id));
						focusOn(null);
					}}
				>
					Remove step
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
};
